/** Ported from chief's `web/src/AppShell.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type ReactNode, useEffect, useState } from 'react';

import { describeProductionSlots, logout, type Stats } from './api.ts';
import { needsAttention, useAppData, useKeyChords, useNow } from './data.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { useNeedsYouNotifications } from './notifications.ts';
import { Link, navigate, useLocation } from './router.tsx';
import { countdown } from './schedule.ts';
import { Gauge, gigabytes, Kbd, Meter } from './ui.tsx';
import { attentionPostsDue, ideasWaiting } from './wording.ts';

/**
 * The frame around every authenticated page: a sidebar with the five places
 * the app has, and the facts an operator wants at all times — how many
 * production slots are busy, what that is costing the machine in CPU and
 * memory, whether Claude Code is signed in, and how long a usage-limit hold
 * still has to run.
 *
 * The sidebar collapses to a top bar below `lg`, where a drawer takes its
 * place; the same list, the same shortcuts.
 */

interface NavItem {
  readonly href: string;
  readonly label: string;
  readonly icon: IconName;
  readonly key: string;
  /** Path prefixes that count as "here". */
  readonly match: readonly string[];
}

const NAV: readonly NavItem[] = [
  { href: '/', label: 'Overview', icon: 'home', key: 'o', match: ['/'] },
  { href: '/content', label: 'Content', icon: 'rocket', key: 'c', match: ['/content'] },
  { href: '/board', label: 'Board', icon: 'project', key: 'k', match: ['/board'] },
  { href: '/ideas', label: 'Ideas', icon: 'bulb', key: 'i', match: ['/ideas'] },
  { href: '/repositories', label: 'Repositories', icon: 'repo', key: 'r', match: ['/repositories'] },
  { href: '/brand-books', label: 'Brand books', icon: 'paintbrush', key: 'b', match: ['/brand-books'] },
  { href: '/ads', label: 'Ads', icon: 'graph', key: 'a', match: ['/ads'] },
  { href: '/library', label: 'Library', icon: 'stack', key: 'l', match: ['/library'] },
  { href: '/terminal', label: 'Terminals', icon: 'terminal', key: 't', match: ['/terminal'] },
  { href: '/settings', label: 'Settings', icon: 'gear', key: ',', match: ['/settings'] },
];

function isCurrent(item: NavItem, pathname: string): boolean {
  if (item.href === '/') return pathname === '/';
  return item.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * What the month has cost, against the monthly cap (US-061).
 *
 * The figure is the ledger's month-to-date and the cap is the one in Settings
 * → Spend, so this row and a refused provider call are reading the same two
 * numbers. It changes appearance once the operator's own **warn me at**
 * threshold is crossed — which is the point of having a threshold at all: it
 * is a warning about a month, early enough to do something about, rather than
 * an error about the call that was finally refused.
 *
 * With no monthly cap set there is nothing to measure against, so the row
 * prints what has been spent and stops there.
 */
function SpendRow({ budget }: { readonly budget: Stats['budget'] | null }) {
  const money = (usd: number): string => `$${usd.toFixed(2)}`;
  const warned = budget?.warned ?? false;
  const title =
    budget === null
      ? 'Provider spend this month'
      : budget.capUsd === null
        ? `${money(budget.spentUsd)} spent on providers in ${budget.month}. No monthly cap is set.`
        : `${money(budget.spentUsd)} of the ${money(budget.capUsd)} monthly cap spent in ${budget.month}` +
          `${budget.warnAtUsd === null ? '' : `; warning at ${money(budget.warnAtUsd)} (${String(budget.warnThresholdPercent)}%)`}.`;
  return (
    <Link
      className={`status-row status-row--link${warned ? ' status-row--wait' : ''}`}
      href="/settings#spend"
      title={title}
    >
      <Icon name="tag" />
      <span className="status-row__label">
        Spend
        {warned && <span className="status-row__note">over {String(budget?.warnThresholdPercent ?? 0)}%</span>}
      </span>
      <span className="status-row__value">
        {budget === null
          ? '…'
          : budget.capUsd === null
            ? money(budget.spentUsd)
            : `${money(budget.spentUsd)}/${money(budget.capUsd)}`}
      </span>
      {budget !== null && budget.capUsd !== null && budget.capUsd > 0 && (
        <Gauge value={budget.spentUsd / budget.capUsd} label={title} />
      )}
    </Link>
  );
}

export function AppShell({ children }: { readonly children: ReactNode }) {
  const { pathname } = useLocation();
  const { stats, claude, contents } = useAppData();
  const [open, setOpen] = useState(false);
  useNeedsYouNotifications(contents);

  // The drawer closes on navigation, and the page scrolls back to the top the
  // way a full navigation would have.
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  useKeyChords({
    ...Object.fromEntries(NAV.map((item) => [`g ${item.key}`, () => navigate(item.href)])),
    'g n': () => navigate('/content/new'),
  });

  const hold = stats?.hold.until ?? null;
  // The same test as the list's “Needs you” filter, so the two always agree.
  const attention = contents?.filter(needsAttention).length ?? 0;
  const ideas = stats?.ideas.toDecide ?? 0;
  // Planned posts whose time has come, beside Board where they are posted from.
  const postsDue = stats?.posts.due ?? 0;

  const nav = (
    <nav className="nav" aria-label="Main">
      <ul className="nav__list">
        {NAV.map((item) => (
          <li key={item.href}>
            <Link
              className={`nav__item${isCurrent(item, pathname) ? ' nav__item--current' : ''}`}
              href={item.href}
              aria-current={isCurrent(item, pathname) ? 'page' : undefined}
              title={`g then ${item.key}`}
            >
              <Icon name={item.icon} />
              <span className="nav__label">{item.label}</span>
              {item.href === '/board' && postsDue > 0 && (
                <span className="nav__count nav__count--wait" title={attentionPostsDue(postsDue)}>
                  {postsDue}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );

  const status = (
    <div className="sidebar__status">
      {attention + ideas > 0 && <AttentionRow contents={attention} ideas={ideas} />}
      {hold !== null && <HoldClock until={hold} />}
      <div
        className="status-row"
        title={stats === null ? 'Production slots in use' : describeProductionSlots(stats.productions)}
      >
        <Icon name="zap" />
        <span className="status-row__label">
          Slots
          {stats !== null && stats.productions.queued > 0 && (
            <span className="status-row__note">+{stats.productions.queued} queued</span>
          )}
        </span>
        <span className="status-row__value">
          {stats === null ? '…' : `${String(stats.productions.active)}/${String(stats.productions.max)}`}
        </span>
        {stats !== null && (
          <Meter
            value={stats.productions.active}
            max={stats.productions.max}
            label={describeProductionSlots(stats.productions)}
          />
        )}
      </div>
      <div
        className="status-row"
        title={
          stats === null
            ? 'Host CPU'
            : `Host CPU across ${String(stats.host.cores)} core${stats.host.cores === 1 ? '' : 's'}`
        }
      >
        <Icon name="pulse" />
        <span className="status-row__label">CPU</span>
        <span className="status-row__value">
          {stats === null || stats.host.cpu === null ? '…' : `${String(Math.round(stats.host.cpu * 100))}%`}
        </span>
        {stats !== null && stats.host.cpu !== null && <Gauge value={stats.host.cpu} label="Host CPU" />}
      </div>
      <div className="status-row" title="Host memory in use">
        <Icon name="package" />
        <span className="status-row__label">RAM</span>
        <span className="status-row__value">
          {stats === null ? '…' : `${gigabytes(stats.host.memory.used)}/${gigabytes(stats.host.memory.total)} GB`}
        </span>
        {stats !== null && stats.host.memory.total > 0 && (
          <Gauge value={stats.host.memory.used / stats.host.memory.total} label="Host memory" />
        )}
      </div>
      <SpendRow budget={stats?.budget ?? null} />
      <Link
        className={`status-row status-row--link ${claude === null ? '' : claude.status.authenticated ? 'status-row--ok' : 'status-row--danger'}`}
        href="/settings#claude"
        title={claude?.status.account ?? 'Claude Code sign-in'}
      >
        <span className={`dot ${claude === null ? 'dot--neutral' : claude.status.authenticated ? 'dot--done' : 'dot--danger'}`} />
        <span className="status-row__label">Claude</span>
        <span className="status-row__value">
          {claude === null ? 'checking…' : claude.status.authenticated ? 'signed in' : 'not signed in'}
        </span>
      </Link>
    </div>
  );

  return (
    <div className={`shell${open ? ' shell--drawer-open' : ''}`}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="topbar">
        <button
          type="button"
          className="button button--icon button--quiet"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="sidebar"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          <Icon name={open ? 'x' : 'menu'} />
        </button>
        <Link className="brand" href="/">
          <Mark />
          <span>marketingchief</span>
        </Link>
      </header>

      <aside className="sidebar" id="sidebar">
        <Link className="brand sidebar__brand" href="/">
          <Mark />
          <span>marketingchief</span>
        </Link>
        {nav}
        <div className="sidebar__spacer" />
        {status}
        <div className="sidebar__foot">
          <button
            type="button"
            className="nav__item nav__item--button"
            onClick={() => {
              logout().finally(() => window.location.replace('/login'));
            }}
          >
            <Icon name="sign-out" />
            <span className="nav__label">Log out</span>
          </button>
          <p className="sidebar__hint">
            <Kbd>g</Kbd> then a key jumps: <Kbd>o</Kbd> overview, <Kbd>c</Kbd> content,{' '}
            <Kbd>n</Kbd> new content.
          </p>
        </div>
      </aside>
      {open && <button type="button" className="scrim" aria-label="Close menu" onClick={() => setOpen(false)} />}

      <main className="content" id="main">
        {children}
      </main>
    </div>
  );
}

/**
 * How many contents and ideas wait on the operator, wherever they are in the
 * app. It opens the contents that need them, or the Ideas page when only ideas
 * wait for a decision (US-125).
 */
function AttentionRow({ contents, ideas }: { readonly contents: number; readonly ideas: number }) {
  const title = [
    contents > 0 ? `${String(contents)} ${contents === 1 ? 'content needs' : 'contents need'} you` : null,
    ideas > 0 ? ideasWaiting(ideas) : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');
  return (
    <Link
      className="status-row status-row--link status-row--wait"
      href={contents === 0 ? '/ideas' : '/content?filter=attention'}
      title={title}
    >
      <Icon name="alert" />
      <span className="status-row__label">Needs you</span>
      <span className="status-row__value">{contents + ideas}</span>
    </Link>
  );
}

/** The usage-limit hold, counting down in the sidebar wherever the operator is. */
function HoldClock({ until }: { readonly until: string }) {
  const now = useNow();
  return (
    <Link
      className="status-row status-row--link status-row--wait"
      href="/content?filter=attention"
      title="Claude’s usage limit was reached; productions resume when the hold lifts"
    >
      <Icon name="clock" />
      <span className="status-row__label">On hold</span>
      <span className="status-row__value mono">{countdown(until, now)}</span>
    </Link>
  );
}

/** The wordmark's diamond: the favicon, at text size. */
export function Mark() {
  return (
    <svg className="mark" viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
      <rect width="32" height="32" rx="7" fill="var(--color-surface-sunken)" />
      <rect x="0.5" y="0.5" width="31" height="31" rx="6.5" fill="none" stroke="var(--color-line-default)" />
      <path d="M16 7.5 24.5 16 16 24.5 7.5 16Z" fill="var(--color-accent-fg)" />
      <path d="M16 12.5 19.5 16 16 19.5 12.5 16Z" fill="var(--color-surface-sunken)" />
    </svg>
  );
}
